import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useGymBilling } from '../../../contexts/GymBillingContext';
import toast from 'react-hot-toast';
import { Sparkles, Download, Dumbbell, Loader2, Clock, Target } from 'lucide-react';

const DIFFICULTY_LABELS = {
  beginner: 'Principiante',
  intermediate: 'Intermedio',
  advanced: 'Avanzato',
  expert: 'Expert',
};

/**
 * Sezione template precompilati per palestre in trial attivo.
 * Modifica minima: componente isolato importato in TemplatesManager.
 */
export default function TrialStarterTemplatesSection({ onImported }) {
  const { getAuthenticatedApi } = useAuth();
  const { isTrialing } = useGymBilling();
  const api = getAuthenticatedApi();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const fetchTrialTemplates = useCallback(async () => {
    if (!isTrialing) return;
    try {
      setLoading(true);
      const res = await api.get('/admin_trial_workout_templates.php?action=trial_list');
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err.response?.status !== 403) {
        console.error(err);
      }
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [api, isTrialing]);

  useEffect(() => {
    fetchTrialTemplates();
  }, [fetchTrialTemplates]);

  if (!isTrialing) {
    return null;
  }

  const handleImport = async (template) => {
    try {
      setImportingId(template.id);
      const res = await api.post('/admin_trial_workout_templates.php?action=import_to_gym', {
        template_id: template.id,
      });
      if (res.data?.success) {
        toast.success(`"${template.name}" importato nella tua libreria`);
        setTemplates((prev) => prev.filter((t) => t.id !== template.id));
        onImported?.();
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || err.response?.data?.error;
      if (err.response?.data?.error === 'ALREADY_IMPORTED') {
        setTemplates((prev) => prev.filter((t) => t.id !== template.id));
        toast.error(msg || 'Template già importato nella tua libreria');
      } else {
        toast.error(msg || 'Errore durante l\'importazione del template');
      }
    } finally {
      setImportingId(null);
    }
  };

  if (loading) {
    return (
      <div className="mb-6 p-4 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/80 dark:bg-amber-950/30">
        <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Caricamento template di prova...</span>
        </div>
      </div>
    );
  }

  if (templates.length === 0) {
    return null;
  }

  return (
    <div className="mb-8 rounded-xl border border-amber-200 dark:border-amber-700/50 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/20 p-5">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Template di prova
        </h2>
        <span className="text-xs bg-amber-200 dark:bg-amber-800/60 text-amber-900 dark:text-amber-100 px-2 py-0.5 rounded-full">
          Solo trial
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Schede precompilate pronte all&apos;uso. Importa un template nella tua libreria e assegnalo ai clienti.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((template) => (
          <div
            key={template.id}
            className="flex flex-col rounded-lg border border-amber-100 dark:border-amber-800/40 bg-white dark:bg-gray-900 p-4"
          >
            <h3 className="font-medium text-gray-900 dark:text-white mb-1">{template.name}</h3>
            {template.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                {template.description}
              </p>
            )}
            <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
              <span className="inline-flex items-center gap-1">
                <Dumbbell className="h-3 w-3" />
                {template.exercise_count || 0} esercizi
              </span>
              {template.estimated_duration && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />~{template.estimated_duration} min
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Target className="h-3 w-3" />
                {DIFFICULTY_LABELS[template.difficulty_level] || template.difficulty_level}
              </span>
            </div>
            <button
              type="button"
              onClick={() => handleImport(template)}
              disabled={importingId === template.id}
              className="mt-auto inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white rounded-lg transition-colors"
            >
              {importingId === template.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Importa in libreria
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
